// The screen's side of live editing: one Y.Doc per open document, connected to
// the server's live copy when its name is known.
//
// Promise:
// - The host decides the transport (`LiveTransport`): the desktop app passes a
//   MessagePort socket class, a browser over HTTP and cloud a ws(s) URL.
// - A document is a Y.Doc first and a connection second: a new document is
//   written into its Y.Doc before it exists, and `connect` later joins the same
//   Y.Doc to the live copy, so the editor bound to it is never made again.
// - The connection names the editor generation (`g<generation>`). A refused
//   connection (an old epoch after a rebuild, another generation, no longer
//   writable) is reported once through `onRefused`; the screen asks the server
//   for the current name and starts again, or asks the person to reload.
// - A host may keep each live copy on the device (`LiveTransport.keep`): a
//   copy kept from before is loaded into the Y.Doc at once and counts as
//   synced (it can be written while the server is not reached); what is
//   written is kept again, and the server and the kept copy merge when they
//   meet (Yjs updates, not the text).
import {
  HocuspocusProvider,
  HocuspocusProviderWebsocket,
} from "@hocuspocus/provider";
import { EDITOR_GENERATION } from "@trameria/editor";
import * as Y from "yjs";

/** How this host reaches live editing. */
export interface LiveTransport {
  /** The socket URL (`ws(s)://<host>/collab`, or the app's own scheme). */
  url: string;
  /** A WebSocket-like class to use instead of the browser's (desktop MessagePort). */
  WebSocketPolyfill?: unknown;
  /** Keeps each live copy on the device, by its name (the phone app). */
  keep?: LiveKeep;
}

/** A live copy's state kept on the device (a Yjs update), by its name. */
export interface LiveKeep {
  load(name: string): Promise<Uint8Array | null>;
  save(name: string, state: Uint8Array): void;
}

/** How long writing pauses before the kept copy is written again. */
const KEEP_AFTER_MS = 500;

export type LiveStatus = "local" | "connecting" | "connected" | "disconnected";

export interface LiveDoc {
  ydoc: Y.Doc;
  /** The live copy's name once connected (null while only local). */
  name: string | null;
  provider: HocuspocusProvider | null;
  destroy(): void;
}

export function createLiveDoc(): LiveDoc {
  const ydoc = new Y.Doc();
  const doc: LiveDoc = {
    ydoc,
    name: null,
    provider: null,
    destroy() {
      const ws = doc.provider?.configuration.websocketProvider;
      doc.provider?.destroy();
      ws?.destroy();
      ydoc.destroy();
    },
  };
  return doc;
}

export interface ConnectHandlers {
  onStatus(status: LiveStatus): void;
  /** The first sync with the live copy finished. */
  onSynced(): void;
  /** The server refused this name (called once). */
  onRefused(name: string): void;
}

/** Joins `doc` to the live copy `name` (once; later calls do nothing). */
export function connectLiveDoc(
  doc: LiveDoc,
  transport: LiveTransport,
  name: string,
  handlers: ConnectHandlers,
): void {
  if (doc.provider) return;
  const websocketProvider = new HocuspocusProviderWebsocket({
    url: transport.url,
    ...(transport.WebSocketPolyfill
      ? {
          WebSocketPolyfill: transport.WebSocketPolyfill as never,
          // A port arrives at once; do not wait a second between attempts.
          delay: 100,
          minDelay: 100,
        }
      : {}),
  });
  let refused = false;
  const provider = new HocuspocusProvider({
    websocketProvider,
    name,
    document: doc.ydoc,
    token: `g${EDITOR_GENERATION}`,
    onStatus: ({ status }) =>
      handlers.onStatus(
        status === "connected"
          ? "connected"
          : status === "connecting"
            ? "connecting"
            : "disconnected",
      ),
    onSynced: ({ state }) => {
      if (state) handlers.onSynced();
    },
    onAuthenticationFailed: () => {
      if (refused) return;
      refused = true;
      handlers.onRefused(name);
    },
  });
  provider.attach();
  doc.provider = provider;
  doc.name = name;
  const keep = transport.keep;
  if (keep) keepLiveDoc(doc, keep, name, handlers);
}

function keepLiveDoc(
  doc: LiveDoc,
  keep: LiveKeep,
  name: string,
  handlers: ConnectHandlers,
): void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const save = () => {
    timer = null;
    keep.save(name, Y.encodeStateAsUpdate(doc.ydoc));
  };
  doc.ydoc.on("update", () => {
    if (timer === null) timer = setTimeout(save, KEEP_AFTER_MS);
  });
  doc.ydoc.on("destroy", () => {
    if (timer !== null) {
      clearTimeout(timer);
      save();
    }
  });
  void keep
    .load(name)
    .then((state) => {
      if (!state || doc.ydoc.isDestroyed) return;
      Y.applyUpdate(doc.ydoc, state, "keep");
      handlers.onSynced();
    })
    .catch(() => undefined);
}
